import {
  LearningWordKind,
  VocabularyTestBlock,
  VocabularyTestWord,
  WordSetCategoryType,
  WordStatusServer,
} from 'app/types';

// The app-wide "switch to wide/sidebar layout" breakpoint now lives in the
// theme (single source of truth, shared with WordSet / IrregularVerbs /
// Community). Re-exported here so this page's existing imports keep working.
export { DESKTOP_LAYOUT_BREAKPOINT } from 'theme';

interface WordWithKind {
  dictionaryId?: number;
  dictionaryIrregularVerbId?: number;
  partOfSpeech?: string;
  word: string;
  wordKind?: LearningWordKind;
}

export const formatVocabularyTestWord = ({
  dictionaryIrregularVerbId,
  partOfSpeech,
  word,
  wordKind,
}: WordWithKind) => {
  const trimmedWord = word.trim();
  const looksLikeIrregularVerbForms = /\S+\s+-\s+\S+/.test(trimmedWord);
  const isIrregularVerb =
    wordKind === 'IRREGULAR_VERB' ||
    !!dictionaryIrregularVerbId ||
    partOfSpeech === 'IRREGULAR_VERB' ||
    looksLikeIrregularVerbForms;
  const isVerb = partOfSpeech === 'VERB';

  if (!isIrregularVerb && !isVerb) {
    return word;
  }

  return /^to\s+/i.test(trimmedWord) ? trimmedWord : `to ${trimmedWord}`;
};

export const getVocabularyTestTopicKey = ({
  categoryName,
  subCategoryName,
  wordSetType,
}: {
  categoryName: string;
  subCategoryName: string;
  wordSetType?: WordSetCategoryType;
}) => `${wordSetType || ''}||${categoryName}||${subCategoryName}`;

export const getVocabularyTestWordKey = ({
  dictionaryId,
  dictionaryIrregularVerbId,
  partOfSpeech,
  word,
  wordKind,
}: WordWithKind) =>
  [
    wordKind || 'WORD',
    dictionaryIrregularVerbId || dictionaryId || word,
    partOfSpeech || '',
    word,
  ].join('::');

/**
 * Mark mutators per status. Each one rewrites the legacy booleans so the
 * BE (and any FE consumer that still reads `isLearned`/`isActiveSpeech`)
 * stays coherent during the transition. The `status` field is the new
 * source of truth — the BE entity layer normalises booleans from it on
 * persist.
 */
export const STATUS_MUTATORS: Record<
  WordStatusServer,
  (word: VocabularyTestWord) => VocabularyTestWord
> = {
  ACTIVE: (word) => ({
    ...word,
    status: 'ACTIVE',
    isLearned: true,
    isActiveSpeech: true,
  }),
  LEARNED: (word) => ({
    ...word,
    status: 'LEARNED',
    isLearned: true,
    isActiveSpeech: false,
  }),
  REPEAT: (word) => ({
    ...word,
    status: 'REPEAT',
    // The booleans collapse REPEAT and NEW into the same shape (both
    // false). Status disambiguates — legacy boolean-only readers will
    // see this as "needs to be learned", which is acceptable until
    // they migrate.
    isLearned: false,
    isActiveSpeech: false,
  }),
  NEW: (word) => ({
    ...word,
    status: 'NEW',
    isLearned: false,
    isActiveSpeech: false,
  }),
};

/**
 * Apply a status to every word across all blocks whose key is present
 * (truthy) in `selectedKeys`. Words not present in the selection map are
 * returned unchanged (same reference).
 */
export const applyStatusToSelectedWords = (
  blocks: VocabularyTestBlock[],
  selectedKeys: Record<string, boolean | undefined>,
  status: WordStatusServer,
): VocabularyTestBlock[] => {
  const mutator = STATUS_MUTATORS[status];
  return blocks.map((block) => ({
    ...block,
    words: block.words.map((word) =>
      selectedKeys[getVocabularyTestWordKey(word)] ? mutator(word) : word,
    ),
  }));
};

/**
 * Distributes `items` into `columns` column-major buckets: the first
 * column takes the first `ceil(items.length / columns)` items, the next
 * column takes the next chunk, and so on. Trailing empty columns are
 * dropped (so a 3-word category never renders 2 empty columns next to
 * 3 one-word columns).
 */
export const distributeWordsIntoColumns = <T>(
  items: T[],
  columns: number,
): T[][] => {
  const perColumn = Math.ceil(items.length / columns);
  const result: T[][] = [];
  for (let col = 0; col < columns; col++) {
    const slice = items.slice(col * perColumn, (col + 1) * perColumn);
    if (slice.length) result.push(slice);
  }
  return result;
};

/**
 * Picks a column count (2–4) from a category's word count so small
 * categories don't get stretched into sparse columns and large ones
 * don't collapse into one tall column. Capped at 4 (not 5) so each column
 * stays wide enough that word text never has to truncate — multi-word
 * entries like "movie theater" or "post office" need the room.
 */
export const autoColumnCount = (wordCount: number): number =>
  Math.min(4, Math.max(2, Math.round(wordCount / 5)));
